import type { TrackFreeze } from '@/domain/types';
import { withTrack } from '@/domain/mutate';
import { defineCommand } from './registry';
import { idParam, intParam, stringParam } from './params';

/**
 * Freezing and unfreezing.
 *
 * The commands only record the decision; the render itself happens in the store
 * alongside the other jobs that need an audio context, exactly as importing and
 * recording do. That keeps the command layer pure and keeps freezing undoable
 * as a single step.
 */

export const freezeTrack = defineCommand<{
  trackId: string;
  assetId: string;
  startTicks: number;
  lengthTicks: number;
  signature: string;
}>({
  type: 'track.freeze',
  label: (p, project) => `Freeze "${project.tracks[p.trackId]?.name ?? 'track'}"`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const assetId = idParam(raw.assetId);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER, 0);
    const lengthTicks = intParam(raw.lengthTicks, 0, Number.MAX_SAFE_INTEGER, 0);
    if (!trackId || !assetId || startTicks === null || lengthTicks === null) return null;
    // The signature can be large; it is opaque text and only ever compared.
    return { trackId, assetId, startTicks, lengthTicks, signature: stringParam(raw.signature, 200000, '') ?? '' };
  },
  apply: (project, p) => {
    if (!project.assets[p.assetId]) return project;
    const freeze: TrackFreeze = {
      assetId: p.assetId,
      startTicks: p.startTicks,
      lengthTicks: p.lengthTicks,
      signature: p.signature,
      createdAt: Date.now(),
    };
    return withTrack(project, p.trackId, (track) => (track.kind === 'master' ? track : { ...track, freeze }));
  },
});

export const unfreezeTrack = defineCommand<{ trackId: string }>({
  type: 'track.unfreeze',
  label: (p, project) => `Unfreeze "${project.tracks[p.trackId]?.name ?? 'track'}"`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    return trackId ? { trackId } : null;
  },
  apply: (project, p) => {
    // The rendered file is left in the project's assets. Garbage collection
    // sweeps it once nothing references it, and until then re-freezing an
    // unchanged track can reuse it.
    return withTrack(project, p.trackId, (track) => (track.freeze ? { ...track, freeze: null } : track));
  },
});
